import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Trophy, Medal, Star, Target, Flame, Zap, TrendingUp, Crown, BookOpen, Brain, Film, Users, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = {
  'trophy': Trophy, 'medal': Medal, 'star': Star, 'target': Target,
  'flame': Flame, 'fire': Flame, 'zap': Zap, 'trending-up': TrendingUp,
  'crown': Crown, 'book-open': BookOpen, 'notebook': BookOpen,
  'brain': Brain, 'film': Film, 'users': Users
};

const Achievements = ({ isDark }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAchievements(); }, []);

  const fetchAchievements = async () => {
    try {
      const res = await axios.get(`${API}/achievements/`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center h-64 items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="achievements-page">
      <div className="flex items-center justify-between">
        <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
          <Trophy className="h-5 w-5 text-yellow-500" /> Achievements
        </h2>
        <Badge variant="secondary" className="text-sm">{data?.unlocked || 0} / {data?.total || 0}</Badge>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div className="bg-yellow-500 h-2.5 rounded-full transition-all" style={{ width: `${((data?.unlocked || 0) / (data?.total || 1)) * 100}%` }} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data?.badges?.map((badge) => {
          const Icon = ICON_MAP[badge.icon] || Trophy;
          return (
            <Card key={badge.id} className={`transition-all ${badge.unlocked ? 'border-yellow-300 bg-yellow-50/50' : 'opacity-60 grayscale'}`} data-testid={`badge-${badge.id}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${badge.unlocked ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                  {badge.unlocked ? <Icon className="h-5 w-5 text-yellow-600" /> : <Lock className="h-4 w-4 text-gray-400" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{badge.name}</p>
                  <p className="text-xs text-gray-500">{badge.description}</p>
                </div>
                {badge.unlocked && <Badge className="bg-yellow-500 text-white text-[9px] shrink-0 ml-auto">Unlocked</Badge>}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Achievements;
